/** OneDrive / Google Drive tab: link screen (superadmin) → full in-app browser once linked. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Cloud, Folder, FileText, Image as ImageIcon, FileSpreadsheet, Film, File as FileIcon, Search, Upload, FolderPlus, Download, ExternalLink, Trash2, ChevronRight, Loader2, Unlink, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAMES = { onedrive: "OneDrive", google: "Google Drive" };
const fmtSize = (n) => (!n ? "" : n < 1048576 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1048576).toFixed(1)} MB`);
const iconFor = (it) => {
  if (it.folder) return Folder;
  const m = it.mime || "";
  if (m.startsWith("image/")) return ImageIcon;
  if (m.startsWith("video/")) return Film;
  if (m.includes("sheet") || m.includes("excel") || m.includes("csv")) return FileSpreadsheet;
  if (m.includes("pdf") || m.includes("document") || m.includes("word") || m.startsWith("text/")) return FileText;
  return FileIcon;
};

function ConnectScreen({ provider, status, onConnected }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/cloud/${provider}/connect`); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start sign-in"); setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center" data-testid={`cloud-connect-${provider}`}>
      <Cloud className="mx-auto h-10 w-10 text-[#4f6fe8]" />
      <h3 className="mt-3 text-base font-bold text-white">Link {NAMES[provider]}</h3>
      {status.can_connect ? (
        <>
          <p className="mt-2 text-sm text-white/60">Sign in with the company {provider === "google" ? "Google" : "Microsoft"} account once. Owners and admins will then browse, upload and download its files right here.</p>
          {!status.configured && <p className="mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-2 text-xs text-amber-200" data-testid="cloud-not-configured">Client ID / secret not set yet — add them under Admin Settings → API ({NAMES[provider]} section), then come back.</p>}
          <button onClick={connect} disabled={busy || !status.configured} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#1a3fd1] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="cloud-connect-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />} Sign in to {NAMES[provider]}
          </button>
        </>
      ) : (
        <p className="mt-2 text-sm text-white/60" data-testid="cloud-connect-locked">A superadmin needs to link the company {NAMES[provider]} account first. Once linked, it shows up here for owners and admins.</p>
      )}
    </div>
  );
}

function Preview({ provider, item, onClose, onDeleted }) {
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/cloud/${provider}/file/${encodeURIComponent(item.id)}/download?inline=1`;
  const isImg = (item.mime || "").startsWith("image/"), isPdf = item.mime === "application/pdf" || item.google_doc, isVid = (item.mime || "").startsWith("video/");
  const download = async () => {
    try {
      const { data } = await api.get(`/cloud/${provider}/file/${encodeURIComponent(item.id)}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = Object.assign(document.createElement("a"), { href: url, download: item.google_doc ? `${item.name}.pdf` : item.name });
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Download failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Move "${item.name}" to the ${provider === "google" ? "trash" : "recycle bin"}?`)) return;
    try { await api.delete(`/cloud/${provider}/file/${encodeURIComponent(item.id)}`); toast.success("Moved to trash"); onDeleted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="flex h-full flex-col rounded-xl border border-white/10 bg-[#0b1220]" data-testid="cloud-preview">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-white" data-testid="cloud-preview-name">{item.name}</div>
        <button onClick={download} className="rounded-md border border-white/15 p-1.5 text-white/70" title="Download" data-testid="cloud-preview-download"><Download className="h-4 w-4" /></button>
        {item.web_url && <a href={item.web_url} target="_blank" rel="noreferrer" className="rounded-md border border-white/15 p-1.5 text-white/70" title={`Open in ${NAMES[provider]}`} data-testid="cloud-preview-open"><ExternalLink className="h-4 w-4" /></a>}
        <button onClick={del} className="rounded-md border border-red-400/40 p-1.5 text-red-300" title="Delete" data-testid="cloud-preview-delete"><Trash2 className="h-4 w-4" /></button>
        <button onClick={onClose} className="rounded-md p-1.5 text-white/50" aria-label="Close preview"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-auto bg-black/30 p-2">
        {isImg ? <img src={src} alt={item.name} className="mx-auto max-h-[70vh] rounded" />
          : isPdf ? <iframe title={item.name} src={src} className="h-[70vh] w-full rounded bg-white" />
            : isVid ? <video src={src} controls className="mx-auto max-h-[70vh]" />
              : <div className="py-16 text-center text-sm text-white/60">No in-app preview for this type — download it or open it in {NAMES[provider]}.</div>}
      </div>
      <div className="border-t border-white/10 px-3 py-1.5 text-[11px] text-white/50">{fmtSize(item.size)}{item.modified ? ` · modified ${new Date(item.modified).toLocaleString()}` : ""}</div>
    </div>
  );
}

export default function CloudDriveTab({ provider }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [folder, setFolder] = useState("root");
  const [crumbs, setCrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [progress, setProgress] = useState({});   // file name → % (chunked uploads)
  const fileRef = useRef(null);

  const loadStatus = useCallback(() => api.get(`/cloud/${provider}/status`).then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, connected: false })), [provider]);
  useEffect(() => {
    loadStatus();
    const p = new URLSearchParams(window.location.search);
    if (p.get("cloud") === provider) {
      if (p.get("cloud_connected")) toast.success(`${NAMES[provider]} linked`);
      if (p.get("cloud_error")) toast.error(p.get("cloud_error"));
      ["cloud", "cloud_connected", "cloud_error"].forEach((k) => p.delete(k));
      window.history.replaceState({}, "", `${window.location.pathname}?${p}`);
    }
  }, [provider, loadStatus]);

  const load = useCallback(async (f = folder) => {
    setLoading(true); setSel(null);
    try { const { data } = await api.get(`/cloud/${provider}/list`, { params: { folder: f } }); setItems(data.items); setCrumbs(data.crumbs || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load files"); }
    finally { setLoading(false); }
  }, [provider, folder]);
  useEffect(() => { if (status?.connected) load(folder); }, [status?.connected, folder, load]);

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return load();
    setLoading(true);
    try { const { data } = await api.get(`/cloud/${provider}/search`, { params: { q } }); setItems(data.items); setCrumbs([{ id: folder, name: `Search: ${q}` }]); }
    catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };
  const BIG = 8 * 1024 * 1024;   // above this, stream in 5 MB chunks straight through to the provider (no size cap)
  const uploadBig = async (f) => {
    const { data: s } = await api.post(`/cloud/${provider}/upload/session`, { name: f.name, size: f.size, folder, mime: f.type || "application/octet-stream" });
    for (let off = 0; off < f.size; off += s.chunk_size) {
      const end = Math.min(off + s.chunk_size, f.size) - 1;
      await api.put(`/cloud/${provider}/upload/session/${s.session_id}/chunk`, f.slice(off, end + 1), { headers: { "Content-Type": "application/octet-stream", "Content-Range": `bytes ${off}-${end}/${f.size}` } });
      setProgress((p) => ({ ...p, [f.name]: Math.round(((end + 1) / f.size) * 100) }));
    }
  };
  const upload = async (files) => {
    for (const f of Array.from(files || [])) {
      setUploading((n) => n + 1); setProgress((p) => ({ ...p, [f.name]: 0 }));
      try {
        if (f.size > BIG) await uploadBig(f);
        else { const fd = new FormData(); fd.append("file", f); await api.post(`/cloud/${provider}/upload`, fd, { params: { folder } }); }
        toast.success(`Uploaded ${f.name}`);
      }
      catch (e) { toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`); }
      finally { setUploading((n) => n - 1); setProgress((p) => { const n = { ...p }; delete n[f.name]; return n; }); }
    }
    load();
  };
  const mkdir = async () => {
    const name = window.prompt("New folder name");
    if (!name) return;
    try { await api.post(`/cloud/${provider}/folder`, { name, folder }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not create folder"); }
  };
  const disconnect = async () => {
    if (!window.confirm(`Unlink ${NAMES[provider]}? Owners and admins will lose access until a superadmin links it again.`)) return;
    try { await api.delete(`/cloud/${provider}/disconnect`); toast.success("Unlinked"); loadStatus(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not unlink"); }
  };

  if (!status) return <div className="py-10 text-center text-sm text-white/50"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  if (!status.connected) return <ConnectScreen provider={provider} status={status} onConnected={loadStatus} />;

  return (
    <div className="space-y-3" data-testid={`cloud-browser-${provider}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto whitespace-nowrap text-white/70" data-testid="cloud-crumbs">
          <button onClick={() => { setQ(""); setFolder("root"); }} className={`rounded px-1.5 py-0.5 font-semibold hover:bg-white/10 ${folder === "root" ? "text-white" : ""}`} data-testid="cloud-crumb-root">{NAMES[provider]}</button>
          {crumbs.map((c) => <span key={c.id} className="flex items-center gap-1"><ChevronRight className="h-3 w-3 text-white/30" /><button onClick={() => { setQ(""); setFolder(c.id); }} className="rounded px-1.5 py-0.5 hover:bg-white/10">{c.name}</button></span>)}
        </nav>
        <form onSubmit={search} className="flex items-center gap-1">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${NAMES[provider]}`} className="w-44 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" data-testid="cloud-search-input" />
          <button type="submit" className="rounded-md border border-white/15 p-1.5 text-white/70" aria-label="Search" data-testid="cloud-search-btn"><Search className="h-4 w-4" /></button>
        </form>
        <button onClick={() => load()} className="rounded-md border border-white/15 p-1.5 text-white/70" aria-label="Refresh" data-testid="cloud-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
        <button onClick={mkdir} className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1.5 font-semibold text-white/80" data-testid="cloud-new-folder"><FolderPlus className="h-4 w-4" /> Folder</button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="cloud-upload-input" />
        <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 font-semibold text-white" data-testid="cloud-upload-btn">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload</button>
        {user?.role === "superadmin" && <button onClick={disconnect} className="rounded-md border border-white/15 p-1.5 text-white/50" title={`Unlink ${status.account?.account || NAMES[provider]}`} data-testid="cloud-disconnect"><Unlink className="h-4 w-4" /></button>}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-white/40" data-testid="cloud-account">
        <span>Linked account: {status.account?.account || "—"}</span>
        {Object.entries(progress).map(([n, pct]) => (
          <span key={n} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-white/70" data-testid="cloud-upload-progress">
            <span className="max-w-[12rem] truncate">{n}</span>
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-white/10"><span className="block h-full bg-[#4f6fe8]" style={{ width: `${pct}%` }} /></span>
            <span className="font-mono">{pct}%</span>
          </span>
        ))}
      </div>

      <div className={`grid gap-3 ${sel ? "lg:grid-cols-[1fr_1.1fr]" : ""}`}>
        <div className="rounded-xl border border-white/10 bg-white/[0.03]" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} data-testid="cloud-items">
          {!loading && !items.length && <div className="py-14 text-center text-sm text-white/50">Empty folder — drop files here or hit Upload.</div>}
          <ul className="divide-y divide-white/5">
            {items.map((it) => { const Icon = iconFor(it); return (
              <li key={it.id} onClick={() => it.folder ? (setQ(""), setFolder(it.id)) : setSel(it)} onDoubleClick={() => !it.folder && it.web_url && window.open(it.web_url, "_blank")}
                className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-white/5 ${sel?.id === it.id ? "bg-[#1a3fd1]/20" : ""}`} data-testid={`cloud-item-${it.id}`}>
                {it.thumbnail && !it.folder ? <img src={it.thumbnail} alt="" className="h-8 w-8 rounded object-cover" /> : <Icon className={`h-5 w-5 ${it.folder ? "text-amber-300" : "text-white/60"}`} />}
                <span className="min-w-0 flex-1 truncate text-white">{it.name}</span>
                <span className="w-16 text-right text-[11px] text-white/40">{it.folder ? (it.child_count != null ? `${it.child_count} items` : "") : fmtSize(it.size)}</span>
                <span className="hidden w-28 text-right text-[11px] text-white/40 sm:block">{it.modified ? new Date(it.modified).toLocaleDateString() : ""}</span>
              </li>); })}
          </ul>
        </div>
        {sel && <Preview provider={provider} item={sel} onClose={() => setSel(null)} onDeleted={() => load()} />}
      </div>
    </div>
  );
}
